import axios from "axios";
import { appConfig } from "@/utils/config";
import { endpoints } from "./endpoints";
import type { TokenPair } from "./tokenStorage";

/**
 * Raw, unauthenticated transport for `/auth/login` and `/auth/refresh`
 * only. Deliberately does NOT import `apiClient` - both of these calls
 * happen precisely when there is no valid access token yet (login) or when
 * the current one has failed (refresh), so they never need the shared
 * client's Bearer-injection interceptor. Kept as its own module (rather
 * than folded into `apiClient.ts`) so `apiClient.ts`'s own response
 * interceptor can call `refresh()` here without importing `authService.ts`
 * (which itself imports `apiClient.ts` for `logout()`) - avoids a circular
 * import between the two.
 */

const transport = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

interface EnvelopeResponse<T> {
  data: T;
}

interface TokenPairPayload {
  access_token: string;
  refresh_token: string;
}

interface LoginResultPayload extends TokenPairPayload {
  mfa_required: boolean;
  mfa_session_id: string | null;
}

function toTokenPair(payload: TokenPairPayload): TokenPair {
  return { accessToken: payload.access_token, refreshToken: payload.refresh_token };
}

export const tokenTransport = {
  async login(email: string, password: string): Promise<TokenPair> {
    const response = await transport.post<EnvelopeResponse<LoginResultPayload>>(
      endpoints.auth.login,
      { email, password },
    );
    return toTokenPair(response.data.data);
  },

  async refresh(refreshToken: string): Promise<TokenPair> {
    const response = await transport.post<EnvelopeResponse<TokenPairPayload>>(
      endpoints.auth.refresh,
      { refresh_token: refreshToken },
    );
    return toTokenPair(response.data.data);
  },
};
